<template>
    <div class="data-table__container h-full">
        <portal :to="`widget-header__${widget.WidgetID}`">
            <div class="flex justify-start">
                <slot name="search-input" />
                <slot name="pagination-rows-per-page" />
            </div>
            <slot name="additional-data" />
        </portal>
        <portal :to="`widget-header__${widget.WidgetID}-action`">
            <div
                v-if="showManageColumns"
                :key="`${widget.WidgetID} - ${activeLanguage}`"
                class="flex items-center table-row__count"
            >
                <el-dropdown
                    size="mini"
                    class="px-1-5 hover:bg-primary-100 rounded"
                    trigger="click"
                >
                    <el-tooltip
                        class="item"
                        effect="dark"
                        :content="$t('datatable.manage.columns')"
                        placement="top"
                    >
                        <span>
                            <i class="vc-icon-filter el-icon--filter text-primary" />
                        </span>
                    </el-tooltip>
                    <template #dropdown>
                        <el-dropdown-menu>
                            <manage-columns
                                :key="`${widget.WidgetID} - ${activeLanguage}`"
                                :available-columns="availableColumns"
                                :visible-columns="visibleColumns"
                                :display-queue-as-rows="displayQueueAsRows"
                                @on-change-visibility="updateColumnsVisibility"
                                @on-reorder-column="reorderColumn"
                            />
                        </el-dropdown-menu>
                    </template>
                </el-dropdown>
            </div>
        </portal>
        <div
            :id="tableId"
            class="bg-transparent rounded-lg data-table w-full"
            :style="cssVars"
        >
            <el-table
                v-if="drawTable"
                :id="tableId"
                :key="tableId"
                ref="table"
                :data="rowsData"
                :fit="fitWidth"
                class="rounded-lg h-full bg-transparent ltr-direction"
                row-key="id"
                v-bind="$attrs"
                v-on="listeners"
            >
                <template #empty>
                    <WidgetNoData :widget="widget" />
                    <h3 class="text-main-xl">
                        {{ $t('general.noData') }}
                    </h3>
                </template>
                <slot name="">
                    <el-table-column
                        v-for="column in renderedColumns"
                        :key="column.prop"
                        class="truncate"
                        :align="column.align"
                        :class-name="column.className"
                        :column-key="column.prop"
                        :fixed="column.fixed || false"
                        :label="makeTableHeaderTranslations(column)"
                        :min-width="column.width || column.minWidth || columnMinWidthData"
                        :sortable="canSortRows"
                        :type="column.type"
                        v-bind="column"
                        :resizable="false"
                    >
                        <template #header>
                            <div class="flex flex-1 w-full break-all">
                                <slot
                                    :column="column"
                                    name="header_title"
                                >
                                    <el-tooltip
                                        :open-delay="300"
                                        placement="top"
                                        :content="makeTableHeaderTranslations(column)"
                                    >
                                        <span class="font-bold flex w-full justify-center uppercase leading-5">
                                            {{ makeTableHeaderTranslations(column) }}
                                        </span>
                                    </el-tooltip>
                                </slot>
                            </div>
                        </template>
                        <template #default="{row, $index}">
                            <slot
                                :index="$index"
                                :name="column.prop || column.type || column.label"
                                :row="row"
                            >
                                <div v-html="formatCell(row, column)" />
                            </slot>
                            <slot
                                :column="column"
                                :index="$index"
                                :row="row"
                                name="realTimeCell"
                            />
                        </template>
                    </el-table-column>
                </slot>
            </el-table>
        </div>
        <portal :to="`widget-actions__${widget.WidgetID}`">
            <div
                v-if="tableData.length"
                class="flex items-center justify-between widget-footer"
            >
                <export-data
                    :table-id="tableId"
                    :widget="widget"
                    v-bind="$attrs"
                    :columns-to-export="columnsToExport"
                    @on-update-layout="updateLayout"
                >
                    <template #default="{onManageExport}">
                        <manage-columns
                            v-if="showManageColumns"
                            :show-header-container="!onManageExport"
                            :available-columns="availableColumns"
                            :visible-columns="visibleColumns"
                            :display-queue-as-rows="displayQueueAsRows"
                            @on-change-visibility="updateColumnsVisibility"
                            @on-reorder-column="reorderColumn"
                        />
                    </template>
                </export-data>
            </div>
        </portal>
        <portal :to="`widget-footer__${widget.WidgetID}`">
            <div
                v-if="tableData.length && !!this.$slots.pagination"
                class="flex items-center justify-end widget-footer -mx-1 h-12"
            >
                <div class="flex">
                    <slot name="pagination" />
                </div>
            </div>
        </portal>
    </div>
</template>

<script setup>
import { activeLanguage } from '@/composables/globalData/useLanguages'
</script>

<script>
import cloneDeep from 'lodash/cloneDeep'
import ExportDataDialog from './ExportData.vue'
import { makeRandomID } from '@/helpers/util'
import { Dropdown, DropdownMenu, Table, TableColumn, Tooltip, Popover, Button } from 'element-ui'
import { dayjs } from '@/composables/useDayjs'
import { DATE_COLUMNS, DATE_TIME_COLUMNS, DATE_FORMAT, DATE_TIME_FORMAT } from '@/helpers/table'
import { pageWidth } from '@/composables/useBreakpoints'
import { widgetTableContentFontSize } from '@/composables/globalData/useLayouts'
import { DEFAULT_FONT_SIZE } from '@/enum/default-layout-config'
import { isNull } from 'lodash'

const QUEUE_STATISTICS_TEMPLATE = 45

export default {
    inheritAttrs: false,
    components: {
        ManageColumns: () => import('./ManageColumns.vue'),
        [Table.name]: Table,
        [Tooltip.name]: Tooltip,
        [Dropdown.name]: Dropdown,
        [TableColumn.name]: TableColumn,
        [DropdownMenu.name]: DropdownMenu,
        [ExportDataDialog.name]: ExportDataDialog,
        [Popover.name]: Popover,
        [Button.name]: Button,
        WidgetNoData: () => import('@/components/Widgets/WidgetNoData.vue')
    },
    props: {
        columnsToDisplay: {
            type: Number,
            default: 0,
        },
        widget: {
            type: Object,
            default: () => ({}),
        },
        tableData: {
            type: Array,
            default: () => [],
        },
        columns: {
            type: Array,
            default: () => [],
        },
        showColumns: {
            type: Array,
            default: () => [],
        },
        editable: {
            type: Boolean,
            default: false,
        },
        displayQueueAsRows: {
            type: Boolean,
            default: false,
        },
        columnsWithPercentage: {
            type: Array,
            default: () => [],
        },
        canSortRows: {
            type: [ Boolean, String ],
            default: false,
        },
        columnMinWidth: {
            type: Number || String,
            default: 170,
        }
    },
    data () {
        const tableId = makeRandomID()
        return {
            columnsToExport: cloneDeep(this.showColumns),
            visibleColumns: cloneDeep(this.showColumns),
            availableColumns: cloneDeep(this.columns),
            tableKey: 'table-key',
            active: false,
            fitWidth: true,
            drawTable: true,
            tableId,
            screenWidth: screen.width,
            showManageColumns: true,
            columnMinWidthData: this.columnMinWidth,
            templateHelp: {}
        }
    },
    computed: {
        listeners () {
            return {
                // eslint-disable-next-line vue/no-deprecated-dollar-listeners-api
                ...this.$listeners,
            }
        },
        renderedColumns () {
            const columns = this.availableColumns
                .filter(c => this.visibleColumns.includes(c.prop))

            if (!this.columnsToDisplay) {
                return columns
            }

            return columns.slice(0, this.columnsToDisplay)
        },
        rowsData () {
            return this.tableData
        },
        getClass () {
            return 'lg:flex-row lg:items-center lg:justify-between'
        },
        currentPageWidth () {
            return pageWidth.value
        },
        dynamicFontSize () {
            return widgetTableContentFontSize.value?.fontSize
        },
        cssVars () {
            const replacePxInString = (string) => {
                return string.replace('px', '')
            }
            const defaultFontSizeNumber = DEFAULT_FONT_SIZE
            const dynamicFontSizeNumber = replacePxInString(this.dynamicFontSize)
            const fontSize = dynamicFontSizeNumber >= defaultFontSizeNumber ? dynamicFontSizeNumber : defaultFontSizeNumber

            return {
                '--dynamic-font-size': fontSize + 'px'
            }
        }
    },
    methods: {
        formatCell (row, column) {
            const columnValue = row[column.prop]
            if (isNull(columnValue) || (typeof columnValue === 'number' && isNaN(columnValue))) {
                return '--'
            }
            if (column.prop.indexOf('%') !== -1 || this.columnsWithPercentage.includes(column.prop)) {
                const _columnValue = parseFloat(columnValue)
                return `${_columnValue} %`
            }

            if (DATE_TIME_COLUMNS.includes(column.prop.toLowerCase())) {
                if (!columnValue) {
                    return '--'
                }
                column['containsDate'] = true
                return this.formatDateColumn(row, column.prop, DATE_TIME_FORMAT)
            }

            if (DATE_COLUMNS.includes(column.prop.toLowerCase())) {
                if (!columnValue) {
                    return '--'
                }
                column['containsDate'] = true
                return this.formatDateColumn(row, column.prop, DATE_FORMAT)
            }
            if (column.prop === 'Stat type') {
                return this.$t(columnValue)
            }
            return columnValue
        },
        formatDateColumn (row, column, dateFormat) {
            if (this.widget.TemplateID.toString() === QUEUE_STATISTICS_TEMPLATE.toString()) {
                return row[column].replace(/\//g, '-')
            }

            try {
                // To prevent dayjs errors like: Invalid time value
                row[column] = row[column].replace('Z', '')
                return dayjs(new Date(row[column])).format(dateFormat)
            } catch (e) {
                return row[column].replace(/\//g, '-')
            }
        },
        updateColumnsVisibility (columns, onManageExport) {
            if (onManageExport) {
                this.columnsToExport = columns
                return
            }
            this.visibleColumns = columns
            this.updateLayout()
        },
        reorderColumn (data, onManageExport) {
            const { element: column, newIndex: newIndex } = data
            let { oldIndex: oldIndex } = data

            oldIndex = this.availableColumns.findIndex((el) => el.prop === column.prop)

            this.availableColumns.splice(oldIndex, 1)
            this.availableColumns.splice(newIndex, 0, column)

            if (onManageExport) {
                oldIndex = this.columnsToExport.findIndex((el) => el.prop === column.prop)

                this.columnsToExport.splice(oldIndex, 1)
                this.columnsToExport.splice(newIndex, 0, column.prop)

                return
            }

            this.updateLayout()
        },
        toggleManageColumns () {
            this.showManageColumns = false
            this.$nextTick(() => {
                this.showManageColumns = true
            })
        },
        updateLayout (afterExport = false) {
            let objToEmit = {}

            if (!this.displayQueueAsRows) {
                objToEmit = {
                    visibleColumns: afterExport ? this.columnsToExport : this.visibleColumns,
                    availableColumns: this.availableColumns,
                }
            } else {
                objToEmit = {
                    visibleQueuesColumns: afterExport ? this.columnsToExport : this.visibleColumns,
                    availableQueuesColumns: this.availableColumns,
                }
            }

            this.drawTable = false
            this.$nextTick(() => {
                this.drawTable = true
            })

            this.$emit('on-update-layout', objToEmit)
        },
        adaptColumnWidth (scale, pageWidth) {
            if (scale == 0.9) {
                this.columnMinWidthData = 150
                return
            }
            if (scale == 0.8) {
                this.columnMinWidthData = 130
                return
            }
            if (scale == 0.75) {
                this.columnMinWidthData = 120
                return
            }
            if (scale == 0.67 || scale < 0.34) {
                this.columnMinWidthData = 90
                return
            }
            return (this.screenWidth / pageWidth).toFixed(2) * 170
        },
        clearDataSort () {
            if (this.$refs['table']) {
                this.$refs['table'].clearSort()
            }
        },
        makeTableHeaderTranslations (column) {
            const columnProp = column.prop
            const translateOfHeaderTitleInitials = `widget.table.header.title.initials.${columnProp}`
            const translateOfHeaderTitleFull = `widget.table.header.title.full.${columnProp}`

            return this.$te(translateOfHeaderTitleInitials) ?
                this.$t(translateOfHeaderTitleInitials) : this.$te(translateOfHeaderTitleFull) ?
                    this.$t(translateOfHeaderTitleFull) : column.label
        }
    },
    watch: {
        columns: {
            immediate: true,
            handler (newColumns) {
                this.availableColumns = cloneDeep(newColumns)
            },
        },
        showColumns: {
            immediate: true,
            handler (columns) {
                this.visibleColumns = cloneDeep(columns)
                this.columnsToExport = cloneDeep(columns)
            },
        },
        currentPageWidth: {
            immediate: true,
            handler (value) {
                const scale = (this.screenWidth / value).toFixed(2)
                this.adaptColumnWidth(scale, value)
            },
        },
        widget: {
            deep: true,
            handler () {
                this.toggleManageColumns()
            },
            immediate: true
        }
    }
}
</script>
<style lang="scss" scoped>
colgroup {
    display: none !important;
}

.el-dropdown-menu--mini {
    padding: 0;

    .popper__arrow {
        display: none !important;
    }
}

.export-button :hover {
    color: var(--primary-color);
}

th > div.cell > span {
    word-break: initial;
}
</style>
<style scoped lang="scss">
.data-table ::v-deep .sortable-ghost {
    opacity: 0.3;
    @apply bg-gray-300 rounded text-primary cursor-pointer;
    td:hover {
        @apply bg-gray-100;
        @apply cursor-pointer;
    }
}

.el-table ::v-deep th:hover.el-table__cell > .cell {
    @apply flex;
}

.data-table ::v-deep .el-table td.direction-ltr {
    direction: ltr;
}

.el-icon--filter {
    @apply text-xl;
}

::v-deep .cell div > a {
    @apply text-primary underline;
}

::v-deep .el-table td > .cell {
    @apply text-black font-medium text-sm;
    font-size: var(--dynamic-font-size);
    line-height: 1.1;
}

::v-deep .el-table__empty-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
</style>
